import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Spinner from 'react/components/common/Spinner/Spinner';
import Tooltip from 'react/components/common/Tooltip/Tooltip';
import './SelectableBarChart.scss';

const SelectableBarChart = ({
  data,
  chartTitle,
  onSelect,
  onSelectAllToggle,
  noDataMsg,
  deleteFn,
  checkboxes = false,
  showSelectAllToggle = false,
  disabledIds = {},
  emphasisedIds = {},
  showSpinner = false,
  query = {},
}) => {
  const [selectedElementId, setSelectedElementId] = useState(null);
  const [selectAll, setSelectAll] = useState(false);
  const [localData, setLocalData] = useState([]);
  const scrollContainerRef = useRef(null);
  const selectionEnabled = !!onSelect;

  // Handle data changes
  useEffect(() => {
    if (data) {
      setLocalData([...data]);

      // Auto-select first element if selection is enabled
      if (data.length && selectionEnabled && onSelect) {
        selectElement(data[0]);
      }

      // Reset scroll position
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }

      // Reset select all if in checkbox mode
      if (checkboxes && selectAll) {
        setSelectAll(false);
      }
    }
  }, [data]);

  const selectElement = (element) => {
    if (!selectionEnabled || !onSelect) return;
    setSelectedElementId(element.id);
    onSelect(element);
  };

  const isSelected = (element) => element.id === selectedElementId;

  const toggleSelected = (bar, event) => {
    event.stopPropagation();

    // Toggle the selection state
    const updatedBar = { ...bar, selected: !bar.selected };
    const newData = localData.map((item) => (item.id === bar.id ? updatedBar : item));
    setLocalData(newData);

    // Notify parent component via onSelect callback
    if (onSelect) {
      onSelect(updatedBar);
    }
  };

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);

    const queryTitle = query?.title || '';
    const newData = localData.map((row) => {
      if (!row.disabled && !disabledIds[row.id + queryTitle]) {
        return { ...row, selected: newSelectAll };
      }
      return { ...row, selected: false };
    });

    setLocalData(newData);

    if (onSelectAllToggle) {
      const selectedBars = newData.filter((row) => row.selected);
      onSelectAllToggle(newSelectAll, selectedBars);
    }
  };

  const handleDelete = (element, event) => {
    event.stopPropagation();
    if (deleteFn) {
      deleteFn(element.id, element);
    } else {
      // Default delete function
      setLocalData(localData.filter((el) => el && el.id !== element.id));
    }
  };

  const getRowClassName = (element) => {
    const queryTitle = query?.title || '';
    const classes = ['row'];

    if (isSelected(element)) classes.push('selected');
    if (element.disabled || disabledIds[element.id + queryTitle]) classes.push('disabled');
    if (element.emphasised || emphasisedIds[element.id + queryTitle]) classes.push('emphasised');
    if (element.selected) classes.push('checked');
    if (element.deletable) classes.push('deletable');
    if (element.highlighted) classes.push('highlighted');

    return classes.join(' ');
  };

  const scrollContainerHeight = checkboxes && showSelectAllToggle ? 'calc(100% - 34px - 50px)' : 'calc(100% - 34px)';

  return (
    <selectable-bar-chart>
      <div
        className={`selectable-bar-chart-drv ${!selectionEnabled ? 'selection-disabled' : ''} ${
          checkboxes ? 'checkboxes' : ''
        }`}
      >
        {!showSpinner && <div className="chart-title">{chartTitle}</div>}

        {checkboxes && showSelectAllToggle && localData.length > 0 && (
          <div className="select-all-wrapper">
            <div className={`row ${selectAll ? 'checked' : ''}`}>
              <span
                className={`checkbox select-all icon-Circle_normal ${selectAll ? 'icon-Circle_on' : ''}`}
                onClick={handleSelectAll}
                id="select-all-checkbox"
              >
                {selectAll && <i className="icon-Circle_on" />}
              </span>

              <div className="label-container" title="Select All">
                <span className="label select-all-label">Select All</span>
              </div>
            </div>
          </div>
        )}

        {!showSpinner && localData.length > 0 && (
          <div className="scroll-container" style={{ height: scrollContainerHeight }} ref={scrollContainerRef}>
            {localData.map((element, index) => {
              const queryTitle = query?.title || '';
              const isDisabled = element.disabled || disabledIds[element.id + queryTitle];

              return (
                <div
                  key={element.id || index}
                  onClick={() => selectElement(element)}
                  className={getRowClassName(element)}
                >
                  {checkboxes && (
                    <Tooltip content={isDisabled ? element.disabledTooltip : ''}>
                      <span className="tooltip-wrapper">
                        <span
                          className={`checkbox ${element.selected ? 'icon-Circle_on' : 'icon-Circle_normal'}`}
                          onClick={(e) => !isDisabled && toggleSelected(element, e)}
                        >
                          {element.selected && <i className="icon-Circle_on" />}
                        </span>
                      </span>
                    </Tooltip>
                  )}

                  <Tooltip
                    content={element.title}
                    options={{ popperOptions: { modifiers: { flip: { enabled: false } } } }}
                  >
                    <div className="label-container">
                      {element.url ? (
                        <a className="label" href={element.url} target="_blank" rel="noopener noreferrer">
                          {element.title}
                        </a>
                      ) : (
                        <span className="label">{element.title}</span>
                      )}

                      {element.highlighted && (
                        <Tooltip content={element.highlightMarkTooltip}>
                          <span className="highlighted-mark" />
                        </Tooltip>
                      )}
                    </div>
                  </Tooltip>

                  {element.deletable && (
                    <i className="delete-button icon-X_CLEAR" onClick={(e) => handleDelete(element, e)} />
                  )}

                  <Tooltip
                    content={element.tooltip}
                    options={{
                      allowHTML: true,
                      maxWidth: 'max-content',
                      popperOptions: { modifiers: { flip: { enabled: false } } },
                    }}
                  >
                    <div className="bar-container">
                      <div
                        className="tooltip-container"
                        style={{
                          width: `calc((100% - 80px) * ${element.percents / 100} + 80px - 10px)`,
                        }}
                      />
                      <div
                        className="bar"
                        style={{
                          width: `calc((100% - 80px) * ${element.percents / 100} - 10px)`,
                        }}
                      />
                      <span className="values">{element.value}</span>
                    </div>
                  </Tooltip>
                </div>
              );
            })}
          </div>
        )}

        {!showSpinner && (!localData || localData.length === 0) && noDataMsg && (
          <div className="no-data-msg">{noDataMsg}</div>
        )}

        {showSpinner && (
          <div className="spinner-container">
            <Spinner show={true} />
          </div>
        )}
      </div>
    </selectable-bar-chart>
  );
};

SelectableBarChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      percents: PropTypes.number,
      tooltip: PropTypes.string,
      url: PropTypes.string,
      disabled: PropTypes.bool,
      disabledTooltip: PropTypes.string,
      emphasised: PropTypes.bool,
      selected: PropTypes.bool,
      deletable: PropTypes.bool,
      highlighted: PropTypes.bool,
      highlightMarkTooltip: PropTypes.string,
    })
  ),
  chartTitle: PropTypes.string,
  onSelect: PropTypes.func,
  onSelectAllToggle: PropTypes.func,
  noDataMsg: PropTypes.string,
  deleteFn: PropTypes.func,
  checkboxes: PropTypes.bool,
  showSelectAllToggle: PropTypes.bool,
  disabledIds: PropTypes.object,
  emphasisedIds: PropTypes.object,
  showSpinner: PropTypes.bool,
  query: PropTypes.object,
};

export default SelectableBarChart;
